import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import type { FilterState } from "../components/FilterPanel";

/**
 * Helper to update a single string filter param
 */
function setOrDeleteParam(params: URLSearchParams, key: string, value: string | null | undefined): void {
  if (value === undefined) return; // No change requested
  if (value === null) {
    params.delete(key);
  } else {
    params.set(key, value);
  }
}

/**
 * Hook to manage filter state for GroupedBrowsePage with URL param synchronization
 * Allows filters to be shareable via URL and persist across page refreshes
 */
export function useFilterState(yearBounds: { min: number; max: number }) {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse filter state from URL params
  const filterState: FilterState = useMemo(() => {
    const yearMinParam = searchParams.get("yearMin");
    const yearMaxParam = searchParams.get("yearMax");

    return {
      country: searchParams.get("country"),
      genre: searchParams.get("genre"),
      director: searchParams.get("director"),
      language: searchParams.get("language"),
      titleId: searchParams.get("titleId"),
      watchlistOnly: searchParams.get("watchlistOnly") === "true",
      yearRange: [
        yearMinParam ? Number.parseInt(yearMinParam, 10) : yearBounds.min,
        yearMaxParam ? Number.parseInt(yearMaxParam, 10) : yearBounds.max,
      ],
    };
  }, [searchParams, yearBounds]);

  // Update filter state and sync to URL
  const updateFilters = useCallback(
    (updates: Partial<FilterState>) => {
      const newParams = new URLSearchParams(searchParams);

      // Update string filters using helper
      setOrDeleteParam(newParams, "country", updates.country);
      setOrDeleteParam(newParams, "genre", updates.genre);
      setOrDeleteParam(newParams, "director", updates.director);
      setOrDeleteParam(newParams, "language", updates.language);
      setOrDeleteParam(newParams, "titleId", updates.titleId);

      // Update watchlistOnly (boolean to string conversion)
      if (updates.watchlistOnly !== undefined) {
        if (updates.watchlistOnly) {
          newParams.set("watchlistOnly", "true");
        } else {
          newParams.delete("watchlistOnly");
        }
      }

      // Update year range
      if (updates.yearRange !== undefined) {
        const [min, max] = updates.yearRange;
        const isDefaultRange = min === yearBounds.min && max === yearBounds.max;
        if (isDefaultRange) {
          newParams.delete("yearMin");
          newParams.delete("yearMax");
        } else {
          newParams.set("yearMin", min.toString());
          newParams.set("yearMax", max.toString());
        }
      }

      setSearchParams(newParams);
    },
    [searchParams, setSearchParams, yearBounds],
  );

  // Clear all filters
  const clearFilters = useCallback(() => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete("country");
    newParams.delete("genre");
    newParams.delete("director");
    newParams.delete("language");
    newParams.delete("titleId");
    newParams.delete("watchlistOnly");
    newParams.delete("yearMin");
    newParams.delete("yearMax");
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return (
      filterState.country !== null ||
      filterState.genre !== null ||
      filterState.director !== null ||
      filterState.language !== null ||
      filterState.titleId !== null ||
      filterState.watchlistOnly ||
      filterState.yearRange[0] !== yearBounds.min ||
      filterState.yearRange[1] !== yearBounds.max
    );
  }, [filterState, yearBounds]);

  return {
    filterState,
    updateFilters,
    clearFilters,
    hasActiveFilters,
  };
}
